import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import type { ImportItem, ImportStatus } from '../../types';
import { formatBytes, formatFullDate } from '../../lib/format';

/** The pipeline every file walks through, in order. */
const STEPS = ['Importing', 'Processing metadata', 'Generating thumbnails', 'Organizing memories', 'Completed'];

export const SETTLED: ImportStatus[] = ['COMPLETED', 'DUPLICATE', 'FAILED'];

/** Which step is current (index), and how many are finished. */
function progressOf(item: ImportItem): { done: number; active: number | null; label: string } {
  switch (item.status) {
    case 'WAITING':
      // Already imported and queued for processing, or not started yet.
      return item.media
        ? { done: 1, active: null, label: 'Waiting to be processed' }
        : { done: 0, active: null, label: 'Waiting' };
    case 'IMPORTING':
      return { done: 0, active: 0, label: 'Importing' };
    case 'PROCESSING_METADATA':
      return { done: 1, active: 1, label: 'Processing metadata' };
    case 'GENERATING_THUMBNAIL':
      return { done: 2, active: 2, label: 'Generating thumbnails' };
    case 'ORGANIZING':
      return { done: 3, active: 3, label: 'Organizing memories' };
    case 'COMPLETED':
      return { done: 5, active: null, label: 'Completed' };
    case 'DUPLICATE':
      return { done: 5, active: null, label: 'Already in your library' };
    case 'FAILED':
      return { done: 0, active: null, label: 'Failed' };
  }
}

/** Plain-language provenance of the capture date. */
function dateSourceNote(source: string): string | null {
  switch (source) {
    case 'SOURCE_FILE_MTIME':
      return 'No date inside the file, so this is the file’s last-modified date';
    case 'FILE_BIRTHTIME':
    case 'FILE_MTIME':
    case 'UPLOAD':
      return 'No date inside the file, so this is when it was added';
    default:
      return null;
  }
}

function StageTrack({ item }: { item: ImportItem }) {
  const { done, active } = progressOf(item);
  const failed = item.status === 'FAILED';
  const duplicate = item.status === 'DUPLICATE';
  return (
    <div className="flex gap-1" aria-hidden="true">
      {STEPS.map((step, i) => (
        <span
          key={step}
          title={step}
          className={`h-1 flex-1 rounded-full transition-colors duration-500 ${
            failed
              ? 'bg-red-500/40'
              : duplicate
                ? 'bg-white/20'
                : i < done
                  ? 'bg-emerald-400/80'
                  : i === active
                    ? 'animate-pulse bg-accent'
                    : 'bg-white/10'
          }`}
        />
      ))}
    </div>
  );
}

function ImportRow({ item, onRetried }: { item: ImportItem; onRetried: () => void }) {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const { label } = progressOf(item);
  const note = item.media ? dateSourceNote(item.media.capturedAtSource) : null;

  const retry = async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      await api.retryImport(item.id);
      onRetried();
    } catch (err) {
      setRetryError((err as Error).message);
    } finally {
      setRetrying(false);
    }
  };

  return (
    <li className="flex gap-3 py-3 sm:gap-4">
      <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md bg-ink-800 sm:h-14 sm:w-20">
        {item.media?.thumbnailUrl ? (
          <img src={item.media.thumbnailUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-white/25">
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="3" y="5" width="18" height="14" rx="2" />
              <path d="M3 16l5-5 4 4 3-3 6 6" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate text-sm font-medium text-white/90" title={item.filename}>
            {item.filename}
          </p>
          <span
            className={`shrink-0 text-xs ${
              item.status === 'FAILED'
                ? 'text-red-300'
                : item.status === 'COMPLETED'
                  ? 'text-emerald-300'
                  : 'text-white/55'
            }`}
            aria-live="polite"
          >
            {label}
          </span>
        </div>

        <div className="mt-2">
          <StageTrack item={item} />
        </div>

        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-white/40">
          <span>{item.source === 'GOOGLE_DRIVE' ? 'From Google Drive' : 'From this device'}</span>
          {item.fileSize ? <span>{formatBytes(item.fileSize)}</span> : null}
          {item.media && (
            <>
              <span title={note ?? 'Date recorded inside the file'}>
                Taken {formatFullDate(item.media.capturedAt)}
                {note ? '*' : ''}
              </span>
              <span>Added {formatFullDate(item.media.importedAt)}</span>
            </>
          )}
        </div>
        {note && item.status === 'COMPLETED' && <p className="mt-0.5 text-[11px] text-white/30">* {note}</p>}

        {item.status === 'FAILED' && (
          <div className="mt-1.5 flex flex-wrap items-center gap-3">
            <p className="text-xs text-red-200/90">{item.error ?? 'This file couldn’t be imported.'}</p>
            {item.retryable && (
              <button
                onClick={retry}
                disabled={retrying}
                className="focus-ring rounded px-2 py-0.5 text-xs font-semibold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white disabled:opacity-50"
              >
                {retrying ? 'Retrying…' : 'Retry'}
              </button>
            )}
          </div>
        )}
        {retryError && <p className="mt-1 text-xs text-red-200/90">{retryError}</p>}
      </div>
    </li>
  );
}

/** Live list of recent imports from both sources, newest first. */
export default function ImportActivity({ items, onChanged }: { items: ImportItem[]; onChanged: () => void }) {
  if (items.length === 0) return null;

  const counts = {
    completed: items.filter((i) => i.status === 'COMPLETED').length,
    duplicate: items.filter((i) => i.status === 'DUPLICATE').length,
    failed: items.filter((i) => i.status === 'FAILED').length,
    active: items.filter((i) => !SETTLED.includes(i.status)).length,
  };
  const summary = [
    counts.active && `${counts.active} in progress`,
    counts.completed && `${counts.completed} added`,
    counts.duplicate && `${counts.duplicate} already in your library`,
    counts.failed && `${counts.failed} failed`,
  ].filter(Boolean);

  return (
    <section className="mt-12" aria-labelledby="imports-heading">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h2 id="imports-heading" className="text-xl font-bold tracking-tight">
          Recent imports
        </h2>
        <p className="text-sm text-white/50">{summary.join(', ')}</p>
      </div>
      <ul className="divide-y divide-white/5 rounded-xl bg-ink-900 px-4 ring-1 ring-white/5 sm:px-5">
        {items.map((item) => (
          <ImportRow key={item.id} item={item} onRetried={onChanged} />
        ))}
      </ul>
      {counts.active === 0 && counts.completed > 0 && (
        <Link
          to="/"
          className="focus-ring mt-4 inline-block rounded-md bg-white px-5 py-2.5 text-sm font-bold text-black transition hover:bg-white/85"
        >
          Watch your memories
        </Link>
      )}
    </section>
  );
}
